import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

const STRINGS = {
  en: {
    // Nav
    home: "Home",
    hiragana: "Hiragana",
    katakana: "Katakana",
    kanji: "Kanji",
    flashcards: "Flashcards",
    pdfSheets: "PDF Sheets",
    login: "Log in",
    signup: "Sign up",
    logout: "Logout",
    dashboard: "Dashboard",
    // Home
    heroTag: "New · Genkoyoushi PDF Generator",
    heroTitle1: "Learn Japanese",
    heroTitle2: "writing the",
    heroTitle3: "way.",
    heroKawaii: "kawaii",
    heroDesc: "Hiragana, Katakana, and Kanji (N5–N3) — with brush-reveal animations, native audio, flashcard quizzes, and printable Genkoyoushi practice sheets.",
    startHira: "Start with Hiragana",
    getPdfs: "Get Practice PDFs",
    freeLine: "Learn free · no account required",
    everythingYouNeed: "Everything you need",
    bentoTitle: "A bento box of learning tools",
    strokeReveal: "Stroke Reveal",
    strokeRevealBody: "Watch each character come to life with a brush-style reveal, then trace it yourself.",
    nativeAudio: "Native Audio",
    nativeAudioBody: "Hear how every kana and kanji sounds with instant pronunciation.",
    flashcardsFeat: "Flashcards",
    flashcardsBody: "Playful quiz mode with kawaii feedback until it sticks in your memory.",
    genkoyoushiPdfs: "Genkoyoushi PDFs",
    genkoyoushiBody: "Download practice sheets or generate custom ones with the characters you're learning.",
    pickPath: "Pick your path",
    whereStart: "Where do you want to start?",
    hiraDesc: "46 core sounds. The foundation of Japanese.",
    kataDesc: "For foreign words, names, and onomatopoeia.",
    kanjiDesc: "100+ essential kanji from JLPT levels.",
    start: "Start",
    footerTag: "Made with matcha and mochi · 頑張って！",
    // Character pages
    hiraChart: "Hiragana chart",
    kataChart: "Katakana chart",
    kanjiChart: "Kanji chart",
    hiraSubtitle: "The rounded, flowing script for native Japanese words. Tap a character to see it drawn, hear it, and trace it.",
    kataSubtitle: "Sharper, angular script used for foreign words, technical terms, and onomatopoeia.",
    kanjiSubtitle: "Meaningful ideograms with on'yomi and kun'yomi readings. Start with N5 basics, then climb to N3.",
    jlptLevel: "JLPT level:",
    reading: "Reading",
    playPron: "Play pronunciation",
    traceIt: "Trace it yourself ✏️",
    clear: "clear",
    replay: "Replay",
    // Flashcards
    practiceMode: "Practice mode",
    showAnswer: "Show answer",
    hearIt: "Hear it",
    needPractice: "Need practice",
    gotIt: "Got it",
    correct: "Correct!",
    answer: "Answer",
    // PDF
    printPractice: "Print & practice",
    readyPrint: "Ready to print",
    buildYourOwn: "Build your own",
    system: "System",
    columns: "Columns",
    rowsPerChar: "Rows per character",
    includeGuides: "Include trace guides",
    download: "Download",
    pickChars: "Pick characters",
    pickCharsSub: "(optional — leave empty for full set)",
    preview: "Preview (Genkoyoushi style)",
    generating: "Generating…",
    // Dashboard
    welcomeBack: "Welcome back",
    yourDashboard: "Your dashboard",
    hiraMastered: "Hiragana mastered",
    kataMastered: "Katakana mastered",
    kanjiMastered: "Kanji mastered",
    recentlyMastered: "Recently mastered",
    charsPracticed: "characters practiced",
    // Auth
    welcomeBackTitle: "Welcome back",
    signInSave: "Sign in to save your progress.",
    email: "Email",
    password: "Password",
    noAccount: "No account?",
    createAcc: "Create your account",
    trackStreak: "Track your streak and mastered characters.",
    yourName: "Your name",
    passMin: "Password (min 6)",
    haveAccount: "Already have an account?",
    preferSkip: "Prefer to skip?",
    learnGuest: "Learn as guest",
    language: "Language",
    // PDF page extras
    pdfLangLabel: "PDF language",
    pdfLangHelp: "Choose the language for the sheet you're about to download",
    columnsLabel: "Columns",
    rowsLabel: "Rows per character",
    hiraganaFullTitle: "Hiragana · Full Chart",
    hiraganaVowelsTitle: "Hiragana · Vowels",
    hiraganaFullDesc: "All 46 hiragana with trace guides.",
    hiraganaVowelsDesc: "あいうえお × 3 rows of practice.",
    katakanaFullTitle: "Katakana · Full Chart",
    katakanaFullDesc: "All 46 katakana with trace guides.",
    kanjiN5Title: "Kanji N5 Starter Pack",
    kanjiN5Desc: "56 essential N5 kanji.",
    kanjiN4Title: "Kanji N4 Practice",
    kanjiN4Desc: "N4 vocabulary kanji sheet.",
    kanjiN3Title: "Kanji N3 Practice",
    kanjiN3Desc: "Level up to N3 kanji.",
    // Premium / pricing
    premium: "Premium",
    pricing: "Pricing",
    premiumSheets: "Premium practice books",
    premiumSheetsSub: "Deluxe sheets with readings, meanings, stroke counts and pastel reference panels — for serious practice.",
    premiumLocked: "Locked",
    premiumUnlocked: "Unlocked",
    unlockSheet: "Unlock this sheet",
    unlockAll: "Unlock everything",
    buySingle: "Buy this PDF",
    viewPlans: "See plans",
    pricingEyebrow: "Go premium",
    pricingTitle: "Choose your practice plan",
    pricingSub: "Free basics stay free forever. Premium unlocks deluxe practice books with readings, meanings and reference panels.",
    planSingleName: "Single sheet",
    planSingleDesc: "Unlock one premium book of your choice, forever.",
    planMonthlyName: "Monthly pass",
    planMonthlyDesc: "Every premium book, plus new drops each month. Cancel anytime.",
    planLifetimeName: "Lifetime access",
    planLifetimeDesc: "Everything, forever. One payment, zero worries.",
    bestValue: "Best value",
    perMonth: "/ month",
    oneTime: "one-time",
    payOnce: "pay once, keep forever",
    ctaSingle: "Pick a sheet",
    ctaMonthly: "Start monthly pass",
    ctaLifetime: "Get lifetime access",
    featDeluxe: "Deluxe reference panels",
    featReadings: "On'yomi & kun'yomi readings",
    featKanaMaster: "Kana Master book (208 kana)",
    featKanjiMaster: "Kanji Master book (N5→N3)",
    featFuture: "All future premium books",
    featOne: "1 premium book of your choice",
    loginToBuy: "Log in to purchase",
    youArePremium: "You're premium — all books unlocked!",
    currentPlan: "Current plan",
    planFree: "Free",
    planMonthly: "Monthly pass",
    planLifetime: "Lifetime",
    securePay: "Secure checkout by Stripe · EUR",
    // Payment result
    payChecking: "Confirming your payment…",
    paySuccessTitle: "Arigatou! Payment confirmed",
    paySuccessSub: "Your premium books are unlocked and ready to download.",
    payPendingTitle: "Almost there",
    payPendingSub: "We're still waiting for confirmation. This usually takes a few seconds.",
    payFailedTitle: "Payment not completed",
    payFailedSub: "Nothing was charged. You can try again whenever you're ready.",
    payCancelTitle: "Checkout cancelled",
    payCancelSub: "No worries — nothing was charged. Your free sheets are always here.",
    goToSheets: "Go to premium sheets",
    backToPricing: "Back to plans",
    backHome: "Back home",
    // Misc
    charactersCount: "characters",
    heroStat1: "Free basics",
    heroStat2: "KanjiVG strokes",
    heroStat3: "Printable PDFs",
    premiumTeaserTitle: "Level up with deluxe practice books",
    premiumTeaserBody: "Readings, meanings, stroke counts and beautiful pastel reference panels on every page. Buy one sheet, go monthly, or unlock everything forever.",
    loading: "Loading…",
    dailyChallenge: "Daily challenge",
    dailyChallengeBody: "3 random characters to master today. Come back tomorrow for a new set!",
    startChallenge: "Start challenge",
    quickActions: "Quick actions",
    nothingYet: "Nothing yet —",
    startDeck: "start a flashcard deck",
    quiz: "Quiz",
  },
  pt: {
    home: "Início",
    hiragana: "Hiragana",
    katakana: "Katakana",
    kanji: "Kanji",
    flashcards: "Cartões",
    pdfSheets: "PDFs",
    login: "Entrar",
    signup: "Registar",
    logout: "Sair",
    dashboard: "Painel",
    heroTag: "Novo · Gerador de PDF Genkoyoushi",
    heroTitle1: "Aprende a escrita",
    heroTitle2: "japonesa à maneira",
    heroTitle3: ".",
    heroKawaii: "kawaii",
    heroDesc: "Hiragana, Katakana e Kanji (N5–N3) — com animações de pincelada, áudio nativo, questionários com cartões e folhas Genkoyoushi para imprimir.",
    startHira: "Começar com Hiragana",
    getPdfs: "Descarregar PDFs de prática",
    freeLine: "Gratuito · sem necessidade de registo",
    everythingYouNeed: "Tudo o que precisas",
    bentoTitle: "Uma caixa bento de ferramentas",
    strokeReveal: "Ordem dos traços",
    strokeRevealBody: "Vê cada caractere a ganhar vida traço a traço, depois traça tu próprio.",
    nativeAudio: "Áudio nativo",
    nativeAudioBody: "Ouve como cada kana e kanji soam com pronúncia instantânea.",
    flashcardsFeat: "Cartões",
    flashcardsBody: "Modo questionário divertido com feedback kawaii até ficar na memória.",
    genkoyoushiPdfs: "PDFs Genkoyoushi",
    genkoyoushiBody: "Descarrega folhas prontas ou gera as tuas com os caracteres que estás a aprender.",
    pickPath: "Escolhe o teu caminho",
    whereStart: "Por onde queres começar?",
    hiraDesc: "46 sons essenciais. A base do japonês.",
    kataDesc: "Para palavras estrangeiras, nomes e onomatopeias.",
    kanjiDesc: "Mais de 100 kanji essenciais dos níveis JLPT.",
    start: "Começar",
    footerTag: "Feito com matcha e mochi · 頑張って！",
    hiraChart: "Tabela hiragana",
    kataChart: "Tabela katakana",
    kanjiChart: "Tabela de kanji",
    hiraSubtitle: "Escrita arredondada e fluida para palavras nativas japonesas. Toca num caractere para o veres, ouvires e traçares.",
    kataSubtitle: "Traços angulares usados para palavras estrangeiras, termos técnicos e onomatopeias.",
    kanjiSubtitle: "Ideogramas com leituras on'yomi e kun'yomi. Começa pelo N5 e sobe até ao N3.",
    jlptLevel: "Nível JLPT:",
    reading: "Leitura",
    playPron: "Ouvir pronúncia",
    traceIt: "Traça tu próprio ✏️",
    clear: "limpar",
    replay: "Repetir",
    practiceMode: "Modo prática",
    showAnswer: "Ver resposta",
    hearIt: "Ouvir",
    needPractice: "Preciso de praticar",
    gotIt: "Já sei",
    correct: "Correcto!",
    answer: "Resposta",
    printPractice: "Imprimir e praticar",
    readyPrint: "Prontos para imprimir",
    buildYourOwn: "Faz o teu",
    system: "Sistema",
    columns: "Colunas",
    rowsPerChar: "Linhas por caractere",
    includeGuides: "Incluir guias de traço",
    download: "Descarregar",
    pickChars: "Escolhe os caracteres",
    pickCharsSub: "(opcional — deixa vazio para o conjunto completo)",
    preview: "Pré-visualização (estilo Genkoyoushi)",
    generating: "A gerar…",
    welcomeBack: "Bem-vindo",
    yourDashboard: "O teu painel",
    hiraMastered: "Hiragana dominados",
    kataMastered: "Katakana dominados",
    kanjiMastered: "Kanji dominados",
    recentlyMastered: "Dominados recentemente",
    charsPracticed: "caracteres praticados",
    welcomeBackTitle: "Bem-vindo de volta",
    signInSave: "Inicia sessão para guardar o teu progresso.",
    email: "E-mail",
    password: "Palavra-passe",
    noAccount: "Não tens conta?",
    createAcc: "Cria a tua conta",
    trackStreak: "Acompanha a tua sequência e caracteres dominados.",
    yourName: "O teu nome",
    passMin: "Palavra-passe (mín. 6)",
    haveAccount: "Já tens conta?",
    preferSkip: "Preferes saltar?",
    learnGuest: "Aprender como convidado",
    language: "Idioma",
    pdfLangLabel: "Idioma do PDF",
    pdfLangHelp: "Escolhe o idioma da folha que estás prestes a descarregar",
    columnsLabel: "Colunas",
    rowsLabel: "Linhas por caractere",
    hiraganaFullTitle: "Hiragana · Tabela completa",
    hiraganaVowelsTitle: "Hiragana · Vogais",
    hiraganaFullDesc: "Todos os 46 hiragana com guias de traço.",
    hiraganaVowelsDesc: "あいうえお × 3 linhas de prática.",
    katakanaFullTitle: "Katakana · Tabela completa",
    katakanaFullDesc: "Todos os 46 katakana com guias de traço.",
    kanjiN5Title: "Kit inicial Kanji N5",
    kanjiN5Desc: "56 kanji essenciais do N5.",
    kanjiN4Title: "Prática Kanji N4",
    kanjiN4Desc: "Kanji do vocabulário N4.",
    kanjiN3Title: "Prática Kanji N3",
    kanjiN3Desc: "Sobe ao nível N3.",
    premium: "Premium",
    pricing: "Planos",
    premiumSheets: "Livros de prática premium",
    premiumSheetsSub: "Folhas deluxe com leituras, significados, número de traços e painéis de referência pastel — para prática a sério.",
    premiumLocked: "Bloqueado",
    premiumUnlocked: "Desbloqueado",
    unlockSheet: "Desbloquear esta folha",
    unlockAll: "Desbloquear tudo",
    buySingle: "Comprar este PDF",
    viewPlans: "Ver planos",
    pricingEyebrow: "Passa a premium",
    pricingTitle: "Escolhe o teu plano de prática",
    pricingSub: "O essencial continua gratuito para sempre. O Premium desbloqueia livros deluxe com leituras, significados e painéis de referência.",
    planSingleName: "Folha única",
    planSingleDesc: "Desbloqueia um livro premium à tua escolha, para sempre.",
    planMonthlyName: "Passe mensal",
    planMonthlyDesc: "Todos os livros premium, mais novidades todos os meses. Cancela quando quiseres.",
    planLifetimeName: "Acesso vitalício",
    planLifetimeDesc: "Tudo, para sempre. Um pagamento, zero preocupações.",
    bestValue: "Melhor valor",
    perMonth: "/ mês",
    oneTime: "pagamento único",
    payOnce: "paga uma vez, fica para sempre",
    ctaSingle: "Escolher uma folha",
    ctaMonthly: "Começar passe mensal",
    ctaLifetime: "Obter acesso vitalício",
    featDeluxe: "Painéis de referência deluxe",
    featReadings: "Leituras on'yomi e kun'yomi",
    featKanaMaster: "Livro Kana Master (208 kana)",
    featKanjiMaster: "Livro Kanji Master (N5→N3)",
    featFuture: "Todos os futuros livros premium",
    featOne: "1 livro premium à tua escolha",
    loginToBuy: "Inicia sessão para comprar",
    youArePremium: "És premium — todos os livros desbloqueados!",
    currentPlan: "Plano atual",
    planFree: "Gratuito",
    planMonthly: "Passe mensal",
    planLifetime: "Vitalício",
    securePay: "Pagamento seguro via Stripe · EUR",
    payChecking: "A confirmar o teu pagamento…",
    paySuccessTitle: "Arigatou! Pagamento confirmado",
    paySuccessSub: "Os teus livros premium estão desbloqueados e prontos a descarregar.",
    payPendingTitle: "Quase lá",
    payPendingSub: "Ainda estamos à espera da confirmação. Normalmente demora alguns segundos.",
    payFailedTitle: "Pagamento não concluído",
    payFailedSub: "Nada foi cobrado. Podes tentar de novo quando quiseres.",
    payCancelTitle: "Compra cancelada",
    payCancelSub: "Sem stress — nada foi cobrado. As folhas gratuitas continuam aqui.",
    goToSheets: "Ir para as folhas premium",
    backToPricing: "Voltar aos planos",
    backHome: "Voltar ao início",
    charactersCount: "caracteres",
    heroStat1: "Essencial grátis",
    heroStat2: "Traços KanjiVG",
    heroStat3: "PDFs para imprimir",
    premiumTeaserTitle: "Sobe de nível com livros de prática deluxe",
    premiumTeaserBody: "Leituras, significados, número de traços e lindos painéis de referência pastel em cada página. Compra uma folha, passa a mensal ou desbloqueia tudo para sempre.",
    loading: "A carregar…",
    dailyChallenge: "Desafio diário",
    dailyChallengeBody: "3 caracteres aleatórios para dominar hoje. Volta amanhã para um novo conjunto!",
    startChallenge: "Começar desafio",
    quickActions: "Ações rápidas",
    nothingYet: "Ainda nada —",
    startDeck: "começa um baralho de cartões",
    quiz: "Questionário",
  },
};

// Portuguese meanings for kanji (fallback to English if missing)
export const KANJI_PT = {
  "一": "um", "二": "dois", "三": "três", "四": "quatro", "五": "cinco",
  "六": "seis", "七": "sete", "八": "oito", "九": "nove", "十": "dez",
  "日": "sol, dia", "月": "lua, mês", "火": "fogo", "水": "água", "木": "árvore, madeira",
  "金": "ouro, dinheiro", "土": "terra, solo", "人": "pessoa", "女": "mulher", "男": "homem",
  "子": "criança", "学": "estudar", "校": "escola", "先": "antes, à frente", "生": "vida, nascer",
  "山": "montanha", "川": "rio", "大": "grande", "小": "pequeno", "中": "meio, dentro",
  "上": "em cima", "下": "embaixo", "目": "olho", "口": "boca", "手": "mão",
  "足": "pé, perna", "耳": "orelha", "本": "livro, origem", "車": "carro, roda", "時": "tempo, hora",
  "分": "minuto, parte", "年": "ano", "何": "o quê", "行": "ir", "食": "comer",
  "見": "ver, olhar", "聞": "ouvir, escutar", "話": "falar, conversar", "読": "ler", "書": "escrever",
  "入": "entrar", "出": "sair", "来": "vir", "父": "pai", "母": "mãe", "友": "amigo",
  "会": "encontrar", "駅": "estação", "道": "estrada, caminho", "家": "casa", "店": "loja",
  "空": "céu, vazio", "海": "mar, oceano", "雨": "chuva", "風": "vento", "花": "flor",
  "犬": "cachorro", "猫": "gato", "鳥": "pássaro", "魚": "peixe", "肉": "carne",
  "音": "som", "楽": "diversão, música", "茶": "chá", "色": "cor", "赤": "vermelho",
  "青": "azul", "白": "branco", "黒": "preto", "元": "origem", "気": "espírito, energia",
  "心": "coração, mente", "体": "corpo", "力": "poder, força", "兄": "irmão mais velho",
  "姉": "irmã mais velha", "弟": "irmão mais novo", "妹": "irmã mais nova",
  "開": "abrir", "閉": "fechar", "止": "parar", "走": "correr", "歩": "caminhar",
  "知": "saber", "思": "pensar", "答": "responder",
  "愛": "amor", "夢": "sonho", "光": "luz, raio", "星": "estrela", "島": "ilha",
  "森": "floresta", "湖": "lago", "熱": "quente, calor", "冷": "frio", "笑": "rir, sorrir",
  "泣": "chorar", "怒": "raiva", "幸": "felicidade", "味": "sabor, gosto", "料": "material, taxa",
  "理": "razão, lógica", "旅": "viagem", "鉄": "ferro", "橋": "ponte", "公": "público",
  "園": "jardim", "院": "instituição", "病": "doença", "医": "medicina, médico",
  "薬": "remédio", "死": "morte", "神": "deus, espírito", "仏": "Buda", "寺": "templo",
  "宮": "santuário, palácio", "城": "castelo", "旗": "bandeira", "民": "povo, nação",
  "首": "pescoço", "都": "capital", "県": "província", "市": "cidade, mercado",
  "町": "cidade pequena", "村": "vila",
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("lang") || "en";
  });

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  const t = useCallback((key) => STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key, [lang]);

  const kanjiMeaning = useCallback((item) => {
    if (lang === "pt") return KANJI_PT[item.char] || item.meaning;
    return item.meaning;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, kanjiMeaning }),
    [lang, t, kanjiMeaning]
  );

  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
